import { useEffect, useRef, useState } from 'react';
import {
  contractSchema,
  occurrenceEconomicsSchema,
  projectMemberSchema,
  type ProjectMember,
} from '@forecastify/shared';
import { useSession } from '../../session';
import { requestAll } from '../read-all';
import { contractOptions } from './contract-label';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R3 · 3 Nov 2026 · who may own a cost or an invoice: the project's people. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE *FELELŐS* PICKER OFFERS THE PROJECT'S CURRENT, ACTIVE MEMBERS AND NOBODY ELSE** — the
 * server refuses anybody else with `assignee_not_a_project_member` (R3) or `member_not_active`
 * (R2), and offering a name the server will refuse is worse than not offering it. Both dialogs used
 * to offer the whole workspace from `/people`, deactivated people included.
 *
 * ⚠️ **The record's CURRENT owner stays in the list** (`keepCurrent`) even when they have left the
 * project or the company since: a picker missing the value it holds draws blank, and a form that
 * re-saves would read as clearing them.
 */
export interface OwnerChoice {
  readonly id: string;
  readonly name: string;
}

/** Whom a project member row may be offered for: today's window, and not deactivated. */
export function isOfferableMember(member: Pick<ProjectMember, 'active' | 'memberStatus'>): boolean {
  return member.active && member.memberStatus === 'active';
}

/** The offerable people of a project member list, one row per person. */
export function ownerChoicesOf(members: readonly ProjectMember[]): readonly OwnerChoice[] {
  const seen = new Map<string, OwnerChoice>();
  for (const member of members) {
    if (!isOfferableMember(member) || seen.has(member.memberId)) continue;
    seen.set(member.memberId, { id: member.memberId, name: member.displayName });
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * `options`, with the value the record already holds kept at the front when it is not among them —
 * named from `everyone` (a list that still has them), or not at all when nobody knows the name.
 */
export function keepCurrent<T extends { readonly id: string }>(
  options: readonly T[],
  currentId: string,
  everyone: readonly T[],
): readonly T[] {
  if (currentId === '' || options.some((one) => one.id === currentId)) return options;
  const current = everyone.find((one) => one.id === currentId);
  return current === undefined ? options : [current, ...options];
}

/**
 * The sentence for a refused cost or invoice save — R1, R2 and R3's codes by name, everything else
 * the generic one. ⚠️ Branching on `code`, never on `message` (rule 5).
 */
export async function ownerOrLineItemRefusal(
  t: Translate,
  response: Response | null,
): Promise<string> {
  const envelope = (await response?.json().catch(() => null)) as {
    error?: { code?: string; details?: { code?: string; message?: string }[] };
  } | null;
  const code = envelope?.error?.code;
  const name = envelope?.error?.details?.[0]?.message ?? '';
  if (code === 'line_item_closed') return t('error.line_item_closed', { name });
  /* 5 Nov 2026 · a cost's contract, or its period, is checked too (`0257`). */
  if (code === 'contract_not_on_project') return t('error.contract_not_on_project', { name });
  if (code === 'contract_not_recurring') return t('extcost.error.notRecurring');
  if (envelope?.error?.details?.[0]?.code === 'not_a_period_start') {
    return t('extcost.error.notAPeriodStart');
  }
  if (code === 'member_not_active') return t('error.member_not_active', { name });
  if (code === 'assignee_not_a_project_member') {
    return t('error.owner_not_a_project_member', { name });
  }
  /* ⚠️ A stale write is its own sentence: an in-place edit races the live reload. */
  return response?.status === 409 ? t('error.conflict') : t('error.unavailable');
}

/**
 * The owner choices for `projectId`, read from `/projects/:id/members`. `null` while unknown — no
 * project yet, a read in flight, or a read refused — so a caller can fall back rather than draw an
 * empty picker.
 */
export function useProjectOwnerChoices(projectId: string | null): readonly OwnerChoice[] | null {
  const session = useSession();
  /* ⚠️ Through a ref: `session` is a fresh object every render (C20's ten-requests bug). */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const workspaceId = session.workspaceId;
  const [choices, setChoices] = useState<{
    readonly projectId: string;
    readonly list: readonly OwnerChoice[];
  } | null>(null);

  useEffect(() => {
    if (projectId === null || projectId === '' || workspaceId === null) return;
    const alive = { current: true };
    void (async () => {
      const response = await sessionRef.current
        .request(`/projects/${projectId}/members`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = projectMemberSchema.array().safeParse(body?.data);
      if (parsed.success && alive.current) {
        setChoices({ projectId, list: ownerChoicesOf(parsed.data) });
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [projectId, workspaceId]);

  return choices !== null && choices.projectId === projectId ? choices.list : null;
}

/** A contract a cost may be charged to — the option's label, and whether it has periods. */
export interface CostContract {
  readonly id: string;
  /** The contract's NAME, as every contract picker reads (`contractOptions`). */
  readonly code: string;
  /** Its identifier, beside the name. */
  readonly hint: string;
  readonly recurring: boolean;
}

/**
 * 5 Nov 2026 · **the project's contracts, for a cost's link** — read here, not derived from its
 * line items: a contract with no line item yet is still where a cost can go, and the period option
 * needs to know which of them recur. `null` while unknown, as `useProjectOwnerChoices`.
 */
export function useProjectContracts(projectId: string | null): readonly CostContract[] | null {
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const workspaceId = session.workspaceId;
  const [contracts, setContracts] = useState<{
    readonly projectId: string;
    readonly list: readonly CostContract[];
  } | null>(null);

  useEffect(() => {
    if (projectId === null || projectId === '' || workspaceId === null) return;
    const alive = { current: true };
    void (async () => {
      const response = await requestAll(
        sessionRef.current,
        `/contracts?projectId=${encodeURIComponent(projectId)}`,
      ).catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = contractSchema.array().safeParse(body?.data);
      if (!parsed.success || !alive.current) return;
      const mine = parsed.data.filter((one) => one.projectId === projectId);
      const recurring = new Set(
        mine.filter((one) => one.type === 'recurring').map((one) => one.id),
      );
      setContracts({
        projectId,
        list: contractOptions(
          mine.map((one) => ({ id: one.id, name: one.name, identifier: one.identifier })),
        ).map((option) => ({
          id: option.value,
          code: option.label,
          hint: option.hint,
          recurring: recurring.has(option.value),
        })),
      });
    })();
    return () => {
      alive.current = false;
    };
  }, [projectId, workspaceId]);

  return contracts !== null && contracts.projectId === projectId ? contracts.list : null;
}

/**
 * 5 Nov 2026 · **the periods of the contract a cost's period arm has PICKED** — read here, on the
 * pick. Both screens read them for the contract the dialog OPENED on, so a new cost's period list
 * stayed empty whatever contract was chosen, and a moved one listed the old contract's periods.
 * `''` for none.
 */
export function useContractPeriods(
  contractId: string,
): readonly { readonly startsOn: string; readonly label: string }[] {
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const workspaceId = session.workspaceId;
  const [periods, setPeriods] = useState<{
    readonly contractId: string;
    readonly list: readonly { readonly startsOn: string; readonly label: string }[];
  } | null>(null);

  useEffect(() => {
    if (contractId === '' || workspaceId === null) return;
    const alive = { current: true };
    void (async () => {
      const response = await sessionRef.current
        .request(`/contracts/${encodeURIComponent(contractId)}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = occurrenceEconomicsSchema.array().safeParse(body?.data);
      if (!parsed.success || !alive.current) return;
      setPeriods({
        contractId,
        list: parsed.data.map((one) => ({
          startsOn: one.startsOn,
          label: `#${String(one.index)} · ${one.startsOn}`,
        })),
      });
    })();
    return () => {
      alive.current = false;
    };
  }, [contractId, workspaceId]);

  return periods !== null && periods.contractId === contractId ? periods.list : [];
}
